import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaPrint} from "react-icons/fa6";
import HeaderTemplateButton from "../../../../components/button/header-template-button.jsx";
import corporationSearchService from "../../../../services/web/corporation-search-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import ShowPassages from "../components/show-passages.jsx";
import SelectedPassage from "../components/selected-passage.jsx";
import moment from "moment";
import NoResults from "../components/no-results.jsx";
import ModalSelectLanes from "../components/modal-select-lanes.jsx";
import {useCapturePlacesTree} from "../../../../components/tree-lanes/hooks/useCapturePlacesTree.js";
import PrintTable from "../components/print-table.jsx";
import Loader from "../../../../components/template/loader.jsx";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import SearchFields from "../components/search-fields.jsx";
import capturePlacesService from "../../../../services/web/capture-places-service.js";

const initialState = {
    dtStart: "",
    dtEnd: "",
    plate: "",
}

const sanitizePlate = (plate) => (plate ?? "").replace(/\s/g, "");

const CorporationSearchList = () => {
    const printComponentRef = useRef(null);
    const [isLoadingPassage, setIsLoadingPassage] = useState(false)
    const [isLoadingPrint, setIsLoadingPrint] = useState(false)
    const [headerSelected, setHeaderSelected] = useState(-1)
    const [state, setState] = useState(initialState)
    const [links, setLinks] = useState({})
    const [passages, setPassages] = useState({})
    const [selectedIndex, setSelectedIndex] = useState("0")
    const [vehicleDetails, setVehicleDetails] = useState({})
    const [passage, setPassage] = useState({})
    const [sideImage, setSideImage] = useState("")
    const [plate, setPlate] = useState("")
    const [formHashCode, setFormHashCode] = useState("")
    const [printPassages, setPrintPassages] = useState([])
    const [filtersToPrint, setFiltersToPrint] = useState("")
    const [isReadyToPrint, setIsReadyToPrint] = useState(false)
    const [showLanes, setShowLanes] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [treeLaneCheckedStatus, setTreeLaneCheckedStatus] = useState({})
    const [printButtons, setPrintButtons] = useState(false)
    const [appliedFilters, setAppliedFilters] = useState(null)
    const [selectedPassageModalShow, setSelectedPassageModalShow] = useState(false)
    const {
        nodes,
        checkedInitialState,
        isLoading,
        inputCamerasValue
    } = useCapturePlacesTree(capturePlacesService, treeLaneCheckedStatus);

    useEffect(() => {
        if (!nodes.length || Object.keys(treeLaneCheckedStatus).length > 0) return;
        setTreeLaneCheckedStatus(checkedInitialState);
    }, [nodes, checkedInitialState, treeLaneCheckedStatus]);
    const onChange = ({target: {value}}) => {
        setState(prev => ({...prev, plate: value.toUpperCase()}));
    }

    const handleDate = useCallback((value, field) => {
        setState(prev => ({
            ...prev,
            [field]: value || null
        }));
    }, []);

    const handleNewSearch = () => {
        search("1", null, null, true);
    }

    const search = (page, finalDate = null, startDate = null, isNewSearch = false) =>{
        setIsLoadingPassage(true)

        let activeState;
        let activeLanes;

        if (isNewSearch) {
            if (!fieldsValidate()){
                setIsLoadingPassage(false);
                return;
            }

            activeState = { ...state };
            activeLanes = JSON.stringify(inputCamerasValue.values.map(el => String(el.code)));

            if (startDate) activeState.dtStart = startDate;
            if (finalDate) activeState.dtEnd = finalDate;

            setAppliedFilters({ state: activeState, lanes: activeLanes });
        } else {
            if (!appliedFilters) {
                setIsLoadingPassage(false);
                return;
            }
            activeState = appliedFilters.state;
            activeLanes = appliedFilters.lanes;
        }

        const formData = new FormData();

        const form = {
            dtStart:  startDate ? startDate : projectUtils.formatDate(activeState.dtStart, "DD/MM/YYYY hh:mm"),
            dtEnd:  finalDate ? finalDate : projectUtils.formatDate(activeState.dtEnd, "DD/MM/YYYY hh:mm"),
            plate: sanitizePlate(activeState.plate),
            formHashCode: ""
        };

        Object.keys(form).forEach(key => {
            formData.append(key, form[key])
        });

        formData.append("page", page);
        formData.append("windowHeight", window.innerHeight);
        formData.append("lanes", activeLanes);

        const response =  corporationSearchService.list(formData)
        response
            .then(({data}) => {
                const prepareLinks = data.data.links

                prepareLinks.map(l => {
                    l.page = l.url && l.url.split("=")[1];
                    return l
                })
                setLinks(prepareLinks)
                setPassages(data.data.data)
                setPrintPassages([])
                setFormHashCode(data.data.path)
                setPassage({})
                setPrintButtons(true)

                const message = data?.errorMessage

                setIsLoadingPassage(false)

                if (message?.includes("O filtro de tempo foi ajustado")) {
                    projectUtils.swalMessage('info', '', message,  'Ok',
                        false, 'Não', () => {
                            let permittedTime = message.match(/(\d+)/)[0];
                            let permittedFinalDate = moment().toISOString() ;
                            let permittedStartDate = moment().subtract(permittedTime, 'hour').toISOString();

                            setState(prev => ({...prev, dtStart: permittedStartDate, dtEnd: permittedFinalDate }))

                            search("1",
                                projectUtils.formatDate(permittedFinalDate, "DD/MM/YYYY hh:mm"),
                                projectUtils.formatDate(permittedStartDate, "DD/MM/YYYY hh:mm"),
                                true
                            )
                        });
                }
            })
            .catch((error) => {
                setIsLoadingPassage(false)
                console.error(error);

                const {message} = error.response.data;

                let errMessage = message ?? "Não foi possível realizar a pesquisa.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            });
    }

    const fieldsValidate = () => {
        const dtStart_ = moment(state.dtStart, 'DD/MM/YYYY hh:mm');
        const dtEnd_ = moment(state.dtEnd, 'DD/MM/YYYY hh:mm');
        const diffHours = dtEnd_.diff(dtStart_, 'hours', false);
        const diffDays = dtEnd_.diff(dtStart_, 'days', false);
        const plate = sanitizePlate(state.plate);

        if (plate === '') {
            if (state.dtStart === '' || state.dtStart === null) {
                projectUtils.swalErrorMessage("", "O campo \"Data inicial\" é obrigatório.", "Ok");
                return false;
            } else if (state.dtEnd === '' || state.dtEnd === null) {
                projectUtils.swalErrorMessage("", "O campo \"Data final\" é obrigatório.", "Ok");
                return false;
            }else if (diffHours > 48) {
                projectUtils.swalErrorMessage("", "Para pesquisas sem a placa do veículo, o período deve ser no máximo 48 horas.", "Ok");
                return false;
            }
        } else {
            if ((state.dtStart === '' || state.dtEnd === '') && plate.length < 7) {
                projectUtils.swalErrorMessage("", "Para pesquisar uma placa incompleta, o período deve ser no máximo 30 dias.", "Ok");
                return false;
            } else if (plate.includes('*') || plate.includes('?') || plate.includes('+') ||
                (plate.length === 1 && plate.includes('-'))) {
                if (state.dtStart === '' || state.dtStart === null) {
                    projectUtils.swalErrorMessage("", "O campo \"Data inicial\" é obrigatório.", "Ok");
                    return false;
                } else if (state.dtEnd === '' || state.dtEnd === null) {
                    projectUtils.swalErrorMessage("", "O campo \"Data final\" é obrigatório.", "Ok");
                    return false;
                } else if (plate.length === 1 && diffDays > 30) {
                    projectUtils.swalErrorMessage("", "Para pesquisar utilizando uma placa incompleta, o período deve ser de no máximo 30 dias.", "Ok");
                    return false;
                } else if (diffDays > 30) {
                    projectUtils.swalErrorMessage("", "Para pesquisar utilizando uma placa incompleta, o período deve ser de no máximo 30 dias.", "Ok");
                    return false;
                }
            } else {
                if (plate.length < 7 && diffDays > 30) {
                    projectUtils.swalErrorMessage("", "Para pesquisar utilizando uma placa incompleta, o período deve ser de no máximo 30 dias.", "Ok");
                    return false;
                }
            }
        }

        if (!inputCamerasValue.label) {
            projectUtils.swalErrorMessage("", "O campo \"Câmeras\" é obrigatório.", "Ok");
            return false;
        }

        return true;
    }

    const clearFilter = () => {
        setState(initialState)
        setAppliedFilters(null)
        setLinks({})
        setPassages([])
        setPrintPassages([])
        setSideImage("")
        setPassage({})
        setSelectedIndex("0")
        setVehicleDetails({})
        setTreeLaneCheckedStatus(checkedInitialState)
        setPrintButtons(false)
    }

    const getVehicleDetails = async (plate) => {
        if (!plate || plate.length < 7) return;

        try {
            const response = await corporationSearchService.vehicleDetails(plate);

            setVehicleDetails(response.data)
        } catch (error) {
            console.error(error);
            projectUtils.swalErrorMessage('', "Erro ao realizar pesquisa", 'Ok');
        }
    }

    const clickedResult = (index, img, passage) => {
        getVehicleDetails(passage.plate.value)
        setSideImage(img)
        setPassage(passage)
        setSelectedIndex(index)
        setPlate(preparePlate(passage.plate))
    }

    useEffect(() => {
        if (passages && passages.length > 0) {
            clickedResult(0, passages[0].imageUrl, passages[0]);
        }
    }, [passages]);

    const preparePlate = (plate) => {
        let plateDisplay = plate?.formattedValue ?? " - ";

        if (plate?.pattern && plate?.pattern.includes("BRAZIL_MERCOSUL_FAKE")) {
            const match = plateDisplay.match(/([A-Z]{3}\s[0-9])([K-Z])([0-9]{2})/);
            if (match) {
                plateDisplay = (
                    <>
                        {match[1]}
                        <span className="text-red-500">{match[2]}</span>
                        {match[3]}
                    </>
                );
            }
        }

        return plateDisplay
    }

    const print = () => {
        setIsLoadingPrint(true)

        const activeState = appliedFilters ? appliedFilters.state : state;
        const activeLanes = appliedFilters ? appliedFilters.lanes : JSON.stringify(inputCamerasValue.values.map(el => String(el.code)));

        const printLabels = [
            activeState.dtStart ? `<b>Data inicial:</b> ${projectUtils.formatDate(activeState.dtStart, "DD/MM/YYYY hh:mm")}` : null,
            activeState.dtEnd ? `<b>Data final:</b> ${projectUtils.formatDate(activeState.dtEnd, "DD/MM/YYYY hh:mm")}` : null,
            inputCamerasValue?.label ? `<b>Câmeras:</b> ${inputCamerasValue.label}` : null,
            activeState.plate ? `<b>Placa:</b> ${activeState.plate}` : null
        ].filter(Boolean).join(" ")

        setFiltersToPrint(printLabels)

        if (passages.length === 0) {
            setPrintPassages([]);
            setIsLoadingPrint(false)
            setIsReadyToPrint(true)
            return
        }

        const formData = new FormData();

        const form = {
            dtStart:  projectUtils.formatDate(activeState.dtStart, "DD/MM/YYYY hh:mm"),
            dtEnd:  projectUtils.formatDate(activeState.dtEnd, "DD/MM/YYYY hh:mm"),
            plate: sanitizePlate(activeState.plate),
        };

        Object.keys(form).forEach(key => {
            formData.append(key, form[key])
        });

        formData.append("windowHeight", window.innerHeight);
        formData.append("lanes", activeLanes);
        formData.append("page", 1);
        formData.append("perPage", 500);
        formData.append("formHashCode", formHashCode);

        const response =  corporationSearchService.list(formData)
        response
            .then(({data}) => {
                setPrintPassages(data.data.data)

                setIsReadyToPrint(true);
            })
            .catch((error) => {
                console.error(error);
                setIsLoadingPrint(false)

                const {message} = error.response.data;

                let errMessage = message ?? "Não foi possível imprimir.";
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            });
    }

    useEffect(() => {
        if (isReadyToPrint) {
            printComponentRef.current?.print()
            setIsReadyToPrint(false);
        }
    }, [isReadyToPrint]);

    useGlobalKey("Enter", () => {
        if (!showModal) handleNewSearch()
    })

    useGlobalKey("Escape", () => {
        if (showModal) setShowModal(false)
        if (selectedPassageModalShow) setSelectedPassageModalShow(false);
    })

    return (
        <>
            {
                (isLoadingPassage || isLoading || isLoadingPrint) &&
                <Loader textCenter>
                    {
                        isLoadingPrint  &&
                            <div className={"text-white"}>Carregando imagens...</div>
                    }
                </Loader>
            }
            <Template isLoading={false}>
                <Template.Header>
                    <Template.Title>Pesquisa de passagens - Minha corporação</Template.Title>

                    <div className={"flex flex-row h-7.5 gap-2"}>
                        {printButtons &&
                            <>
                                <Button title="Imprimir" onClick={print}>
                                    <FaPrint/>
                                </Button>

                                <HeaderTemplateButton selected={headerSelected} setSelected={setHeaderSelected} />
                            </>
                        }

                    </div>
                </Template.Header>

                <Template.Body>
                    <SearchFields
                        showModal={showModal}
                        setShowModal={setShowModal}
                        state={state} handleDate={handleDate} onChange={onChange} clearFilter={clearFilter}
                        search={handleNewSearch} treeLaneCheckedStatus={treeLaneCheckedStatus} setTreeLaneCheckedStatus={setTreeLaneCheckedStatus}
                        inputCamerasValue={inputCamerasValue} nodes={nodes}/>

                    <NoResults passages={passages}/>

                    <div className="grid grid-cols-12 flex-wrap">
                        <SelectedPassage
                            passages={passages}
                            selectedIndex={selectedIndex}
                            plate={plate}
                            passage={passage}
                            vehicleDetails={vehicleDetails}
                            sideImage={sideImage}
                            clickedResult={clickedResult}
                            setSideImage={setSideImage}
                            setPassage={setPassage}
                            show={selectedPassageModalShow}
                            setShow={setSelectedPassageModalShow}
                        />

                        <ShowPassages
                            passages={passages}
                            links={links}
                            clickedResult={clickedResult}
                            preparePlate={preparePlate}
                            selectedIndex={selectedIndex}
                            search={search}
                        />
                    </div>

                    <ModalSelectLanes
                        show={showLanes}
                        onClose={() => setShowLanes(false)}
                        existingDevices={[]}
                        onConfirm={(toRemove) =>
                            setState((prev) =>
                                prev.filter((d) => !toRemove.some((r) => r.id === d.id))
                            )}
                    />

                    <PrintTable headerSelected={headerSelected}
                                printComponentRef={printComponentRef}
                                setIsLoading={setIsLoadingPrint}
                                printPassages={printPassages}
                                filtersToPrint={filtersToPrint}/>

                </Template.Body>
            </Template>
        </>
    );
};

export default CorporationSearchList;